import React, { useState } from 'react';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { calculatePercentChange } from 'src/utils/app';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useCompanyName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import {
  useTopicTrafficByKeyword,
  GetKeywordMetricsResponseRecord,
  GetKeywordMetricsResponse
} from './hooks/useTopicTrafficByKeyword';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import {
  useTotalTrafficByProduct,
  GetTotalTrafficByProductResponseRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTotalTrafficByProduct';
import { GridButtonGroupBase, GridView } from 'src/components/common/Buttons/GridButtonGroupBase';
import ProductGrid, { GRID_ITEMS_PER_PAGE } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { TrafficType } from '../types';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { isTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { useTopicExportModalStore } from 'src/zustandStores/BeaconPro/Topics/Exports/TopicExportModalStore';
import AppExportModal from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const trafficTitles = {
  totalTraffic: 'Total Traffic',
  organicTraffic: 'Organic Traffic',
  paidTraffic: 'Paid Traffic'
};

export function TopicTrafficByKeywords({
  trafficType,
  buildSubtitle,
  topicType
}: {
  trafficType: TrafficType;
  buildSubtitle?: (args: { response: GetKeywordMetricsResponse[] }) => string;
  topicType?: string;
}) {
  const { response, isLoading } = useTopicTrafficByKeyword({
    sortField: trafficType,
    includeClassification: true,
    topicType
  });

  const searchTermToMetricComparisonMap = response[1].records.reduce((acc, record) => {
    acc.set(record.searchTerm, record);
    return acc;
  }, new Map<string, GetKeywordMetricsResponseRecord>());

  return (
    <Flex flexDirection="column" gap="lg">
      {!isLoading && buildSubtitle && <Text variant="body1">{buildSubtitle({ response })}</Text>}
      <EntityTable
        skeletonRows={20}
        isLoading={isLoading}
        shouldModifyColumnDefs={false}
        rowData={response[0].records}
        columnDefs={[
          {
            field: 'searchTerm',
            headerName: 'Keywords'
          },
          {
            field: trafficType,
            headerName: trafficTitles[trafficType],
            valueFormatter: (_, record: GetKeywordMetricsResponseRecord) => {
              const comparisonTraffic = searchTermToMetricComparisonMap.get(record.searchTerm)?.[trafficType] ?? 0;

              return (
                <MetricCell
                  metricType={METRICTYPE.VOLUME}
                  value={record[trafficType]}
                  percentChange={calculatePercentChange(record[trafficType], comparisonTraffic)}
                />
              );
            }
          },
          {
            field: 'type',
            headerName: 'Type'
          },
          {
            field: 'brand',
            headerName: 'Brand',
            valueFormatter: (_, record: GetKeywordMetricsResponseRecord) => {
              const brand = record?.type === 'Unbranded' ? 'No Brand' : record?.brand;
              return <div>{brand}</div>;
            }
          },
          {
            field: 'topic',
            headerName: 'Feature',
            valueFormatter: (_, record: GetKeywordMetricsResponseRecord) => {
              return (
                <div
                  style={{
                    width: '250px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {record?.topic}
                </div>
              );
            },
            tableColumnProps: {
              style: {
                width: '180px'
              }
            }
          }
        ]}
      />
    </Flex>
  );
}

function TopicTrafficByProduct({ gridView, trafficType }: { gridView: GridView; trafficType: TrafficType }) {
  const [page, setPage] = useState(1);
  const mainEntityType = useMainEntityType();
  const { response, isLoading } = useTotalTrafficByProduct({
    page,
    itemsPerPage: GRID_ITEMS_PER_PAGE
  });

  const stacklineSkuToMetricComparisonMap = response[1].records.reduce((acc, record) => {
    acc.set(record.stacklineSku, record);
    return acc;
  }, new Map<string, GetTotalTrafficByProductResponseRecord>());

  const productGridData = React.useMemo(() => {
    return response[0].records.map((record) => ({
      product: record.product,
      [trafficType]: {
        value: record[trafficType],
        difference:
          record[trafficType] - (stacklineSkuToMetricComparisonMap.get(record.stacklineSku)?.[trafficType] ?? 0)
      }
    }));
  }, [response, trafficType, stacklineSkuToMetricComparisonMap]);

  const totalPages = Math.ceil(productGridData.length / GRID_ITEMS_PER_PAGE);

  const tableData = React.useMemo(() => {
    return response[0].records;
  }, [response]);

  const remainingColumnDefs = [
    ['organicTraffic', 'Organic\nTraffic'],
    ['paidTraffic', 'Paid\nTraffic']

  ];

  if (!['topic', 'topicgroup'].includes(mainEntityType)) {
    remainingColumnDefs.push(['otherTraffic', 'Other\nTraffic']);
  }

  remainingColumnDefs.push(
    ['totalTraffic', 'Total\nTraffic'],
    ['paidAdSpend', 'Paid\nAd Spend', METRICTYPE.MONEY],
    ['paidAdSales', 'Paid\nAd Sales', METRICTYPE.MONEY]
  );

  const collumDefinitions = [
    {
      headerName: 'Products',
      valueFormatter: (_, record: GetTotalTrafficByProductResponseRecord) => (
        <ProductCell stacklineSku={record.stacklineSku} title={record.product.title} />
      ),
      tableColumnProps: {
        width: 300
      }
    },
    ...remainingColumnDefs.map(([field, headerName, metricType]) => ({
      field,
      headerName,
      valueFormatter: (value, record: GetTotalTrafficByProductResponseRecord) => {
        const comparisonTraffic = stacklineSkuToMetricComparisonMap.get(record.stacklineSku)?.[field] ?? 0;

        return (
          <MetricCell
            metricType={(metricType ?? METRICTYPE.VOLUME) as METRICTYPE}
            value={value}
            percentChange={calculatePercentChange(value, comparisonTraffic)}
          />
        );
      }
    }))
  ];

  return (
    <Flex flexDirection="column" gap={gridView === GridView.Tile ? 'mdl' : 'none'} alignItems="flex-end">
      {gridView === GridView.Tile ? (
        <ProductGrid
          loading={isLoading}
          metricType={METRICTYPE.VOLUME}
          page={page}
          data={productGridData}
          fieldName={trafficType}
        />
      ) : (
          <EntityTable
            page={page}
            showPagination={false}
            isLoading={isLoading}
            shouldModifyColumnDefs={false}
            rowData={tableData}
            columnDefs={collumDefinitions}
          />
        )}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onClickNext={() => setPage(Math.min(page + 1, totalPages))}
          onClickPrevious={() => setPage(Math.max(page - 1, 1))}
        />
      )}
    </Flex>
  );
}

export interface TrafficGridTopicsProps {
  trafficType: TrafficType;
}
/**
 * New traffic grid for topic/topic groups features
 */
export function TrafficGridTopics({ trafficType }: TrafficGridTopicsProps) {
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const [selectedDropdown, setSelectedDropdown] = useState<'keyword' | 'product'>('keyword');
  const [gridView, setGridView] = useState<GridView>(GridView.Table);
  const entityType = useMainEntityType();
  const isTopic = isTopicOrTopicGroupType(entityType);
  const entityPhrase = useEntityPhrasing();
  const companyName = useCompanyName();
  const taxonomyText = useTaxonomySubtitleText();

  // Topic-related export state
  const { openModal: openTopicExport } = useTopicExportModalStore();

  // Legacy export state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const getSubtitle = () => {
    return (
      <Flex marginTop="8px">
        <Text variant="body1">
          {`The table displays the ${trafficTitles[trafficType].toLowerCase()} ${
            !isTopic ? `for ${entityPhrase}` : `for ${companyName}`
            }`}
          {taxonomyText}
          {isTopic ? ` in ${entityPhrase}` : ''}
          {` by ${selectedDropdown} during the ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </Flex>
    );
  };

  return (
    <Flex flexDirection="column" gap="lg">
      <Flex flexDirection="column">
        <Flex justifyContent="space-between">
          <Flex gap={6} alignItems="center">
            <Text variant="h4">{`${trafficTitles[trafficType]} by`}</Text>
            <SlDropdownMenu
              options={[
                { id: 'keyword', label: 'Keyword' },
                { id: 'product', label: 'Product' }
              ]}
              buttonVariant="title"
              selectedId={selectedDropdown}
              onChange={({ id }) => setSelectedDropdown(id as 'keyword' | 'product')}
            />
          </Flex>

          <GridButtonGroupBase
            icons={{
              tile: selectedDropdown === 'product',
              table: selectedDropdown === 'product',
              export: true
            }}
            selectedView={gridView}
            onChangeView={setGridView}
            onExport={() => {
              // We have 2 different export modals, one for topic-related exports and one for all others
              if (!isTopic) {
                setIsExportModalOpen(true);
              } else {
                openTopicExport();
              }
            }}
          />
        </Flex>
        <Text>{getSubtitle()}</Text>
      </Flex>
      {selectedDropdown === 'keyword' ? (
        <TopicTrafficByKeywords trafficType={trafficType} topicType={isTopic ? 'All' : 'Standard'} />
      ) : (
          <TopicTrafficByProduct gridView={gridView} trafficType={trafficType} />
        )}
      {isExportModalOpen && <AppExportModal open={isExportModalOpen} handleClose={() => setIsExportModalOpen(false)} />}{' '}
    </Flex>
  );
}
